import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../config";
import { TrendingDown, ArrowRight, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Inline copy table — keeps this hero self-contained and avoids
 * bloating the global translations files for a single hero block.
 * Anything not listed falls back to English.
 */
const COPY = {
  en: {
    tag: "Real-time loss calculator",
    h2_a: "Your customers ask ChatGPT.",
    h2_b: "It doesn't know you exist.",
    h2_c: "Here's the price tag.",
    sub: "35% of buyer-research queries already start inside an AI engine in 2026. Plug your numbers in, watch what you're leaking every single month.",
    f_traffic: "Monthly traffic",
    f_aov: "Avg order value",
    f_vis: "AI visibility score",
    monthly_lost: "Monthly Lost Revenue",
    annual: "That's",
    annual_b: "per year. Every month you wait,",
    annual_c: "walks past your checkout.",
    cta: "Get your EXACT number for $9",
    secure: "Secure Stripe checkout",
    results: "Results in 30 seconds",
    one_time: "One-time payment, no subscription",
    businesses: "businesses scanned",
    snapshots_24h: "Snapshots bought in last 24h",
    scans: "AI visibility audits run",
  },
  sk: {
    tag: "Kalkulačka strát v reálnom čase",
    h2_a: "Tvoji zákazníci sa pýtajú ChatGPT.",
    h2_b: "On o tebe nevie.",
    h2_c: "Toto ťa to stojí.",
    sub: "35 % otázok pri nákupnom research-i sa v 2026 začína v AI engine. Zadaj svoje čísla a uvidíš, koľko ti tečie z čierneho mesiaca.",
    f_traffic: "Mesačná návštevnosť",
    f_aov: "Priemerná hodnota objednávky",
    f_vis: "AI viditeľnosť",
    monthly_lost: "Mesačná strata tržieb",
    annual: "To je",
    annual_b: "ročne. Každý mesiac čakania ťa stojí",
    annual_c: "ktoré ti utečú spred pokladne.",
    cta: "Získaj presné číslo za $9",
    secure: "Bezpečná Stripe platba",
    results: "Výsledok za 30 sekúnd",
    one_time: "Jednorazová platba, žiadne predplatné",
    businesses: "skenov firiem",
    snapshots_24h: "Snapshotov za posledných 24h",
    scans: "AI auditov spustených",
  },
  cs: {
    tag: "Kalkulačka ztrát v reálném čase",
    h2_a: "Tvoji zákazníci se ptají ChatGPT.",
    h2_b: "On o tobě neví.",
    h2_c: "Tohle tě to stojí.",
    sub: "35 % nákupních dotazů začíná v roce 2026 v AI vyhledávači. Zadej svá čísla a uvidíš, kolik ti měsíčně utíká.",
    f_traffic: "Měsíční návštěvnost",
    f_aov: "Průměrná hodnota objednávky",
    f_vis: "AI viditelnost",
    monthly_lost: "Měsíční ztráta tržeb",
    annual: "To je",
    annual_b: "ročně. Každý měsíc čekání tě stojí",
    annual_c: "které ti utečou před pokladnou.",
    cta: "Získej přesné číslo za $9",
    secure: "Bezpečná Stripe platba",
    results: "Výsledek za 30 sekund",
    one_time: "Jednorázová platba, žádné předplatné",
    businesses: "skenů firem",
    snapshots_24h: "Snapshotů za posledních 24h",
    scans: "AI auditů spuštěno",
  },
  de: {
    tag: "Echtzeit-Verlust-Rechner",
    h2_a: "Deine Kunden fragen ChatGPT.",
    h2_b: "Es weiß nicht, dass du existierst.",
    h2_c: "Hier ist der Preis dafür.",
    sub: "35 % der Kaufrecherchen starten 2026 in einer KI-Engine. Gib deine Zahlen ein und sieh, was du jeden Monat verlierst.",
    f_traffic: "Monatlicher Traffic",
    f_aov: "Durchschnittlicher Bestellwert",
    f_vis: "KI-Sichtbarkeit",
    monthly_lost: "Monatlicher Umsatzverlust",
    annual: "Das sind",
    annual_b: "pro Jahr. Jeden Monat, den du wartest,",
    annual_c: "gehen an deinem Checkout vorbei.",
    cta: "Hol dir die EXAKTE Zahl für $9",
    secure: "Sichere Stripe-Zahlung",
    results: "Ergebnis in 30 Sekunden",
    one_time: "Einmalige Zahlung, kein Abo",
    businesses: "Firmen gescannt",
    snapshots_24h: "Snapshots in den letzten 24h",
    scans: "KI-Audits durchgeführt",
  },
  pl: {
    tag: "Kalkulator strat w czasie rzeczywistym",
    h2_a: "Twoi klienci pytają ChatGPT.",
    h2_b: "On nie wie, że istniejesz.",
    h2_c: "Oto cena tej niewidoczności.",
    sub: "35 % zapytań zakupowych w 2026 zaczyna się w silnikach AI. Wpisz swoje liczby i zobacz, ile co miesiąc tracisz.",
    f_traffic: "Miesięczny ruch",
    f_aov: "Średnia wartość zamówienia",
    f_vis: "Widoczność AI",
    monthly_lost: "Miesięczna utracona sprzedaż",
    annual: "To",
    annual_b: "rocznie. Każdy miesiąc czekania to",
    annual_c: "które przechodzą obok twojej kasy.",
    cta: "Pobierz dokładną liczbę za $9",
    secure: "Bezpieczna płatność Stripe",
    results: "Wynik w 30 sekund",
    one_time: "Jednorazowa płatność, bez subskrypcji",
    businesses: "przeskanowanych firm",
    snapshots_24h: "Snapshotów kupionych w 24h",
    scans: "audytów AI uruchomionych",
  },
  hu: {
    tag: "Valós idejű veszteség-kalkulátor",
    h2_a: "A vevőid a ChatGPT-t kérdezik.",
    h2_b: "Az nem tudja, hogy létezel.",
    h2_c: "Itt az ára.",
    sub: "A vásárlói kérdések 35 %-a 2026-ban már AI keresőben indul. Add meg számaidat, és nézd meg, mennyi tűnik el havonta.",
    f_traffic: "Havi forgalom",
    f_aov: "Átlagos rendelési érték",
    f_vis: "AI láthatóság",
    monthly_lost: "Havi elveszett árbevétel",
    annual: "Ez",
    annual_b: "évente. Minden várt hónap",
    annual_c: "halad el a pénztáradtól.",
    cta: "Szerezd meg a pontos számot $9-ért",
    secure: "Biztonságos Stripe fizetés",
    results: "Eredmény 30 másodpercen belül",
    one_time: "Egyszeri fizetés, nincs előfizetés",
    businesses: "cég átvizsgálva",
    snapshots_24h: "Snapshot az elmúlt 24 órában",
    scans: "AI audit lefuttatva",
  },
  fr: {
    tag: "Calculateur de pertes en temps réel",
    h2_a: "Tes clients demandent à ChatGPT.",
    h2_b: "Il ne sait pas que tu existes.",
    h2_c: "Voici le prix à payer.",
    sub: "En 2026, 35 % des recherches d'achat commencent dans un moteur IA. Saisis tes chiffres et vois ce qui te fuit chaque mois.",
    f_traffic: "Trafic mensuel",
    f_aov: "Panier moyen",
    f_vis: "Visibilité IA",
    monthly_lost: "Revenu perdu mensuel",
    annual: "Soit",
    annual_b: "par an. Chaque mois d'attente,",
    annual_c: "passe devant ta caisse.",
    cta: "Obtiens ton chiffre EXACT pour 9 $",
    secure: "Paiement Stripe sécurisé",
    results: "Résultats en 30 secondes",
    one_time: "Paiement unique, sans abonnement",
    businesses: "entreprises scannées",
    snapshots_24h: "Snapshots achetés en 24h",
    scans: "audits IA effectués",
  },
  it: {
    tag: "Calcolatore di perdite in tempo reale",
    h2_a: "I tuoi clienti chiedono a ChatGPT.",
    h2_b: "Non sa che esisti.",
    h2_c: "Ecco il prezzo da pagare.",
    sub: "Nel 2026 il 35 % delle ricerche d'acquisto parte da un motore IA. Inserisci i tuoi numeri e guarda quanto perdi ogni mese.",
    f_traffic: "Traffico mensile",
    f_aov: "Valore medio ordine",
    f_vis: "Visibilità IA",
    monthly_lost: "Ricavi persi al mese",
    annual: "Sono",
    annual_b: "all'anno. Ogni mese di attesa,",
    annual_c: "passa davanti alla tua cassa.",
    cta: "Ottieni il tuo numero ESATTO per 9 $",
    secure: "Checkout Stripe sicuro",
    results: "Risultato in 30 secondi",
    one_time: "Pagamento unico, niente abbonamento",
    businesses: "aziende scansionate",
    snapshots_24h: "Snapshot acquistati nelle ultime 24h",
    scans: "audit IA eseguiti",
  },
  es: {
    tag: "Calculadora de pérdidas en tiempo real",
    h2_a: "Tus clientes le preguntan a ChatGPT.",
    h2_b: "No sabe que existes.",
    h2_c: "Aquí está el precio.",
    sub: "En 2026, el 35 % de las búsquedas de compra empiezan en un motor IA. Mete tus números y mira cuánto se te escapa cada mes.",
    f_traffic: "Tráfico mensual",
    f_aov: "Valor medio del pedido",
    f_vis: "Visibilidad IA",
    monthly_lost: "Ingresos perdidos al mes",
    annual: "Eso es",
    annual_b: "al año. Cada mes que esperas,",
    annual_c: "pasa de largo tu caja.",
    cta: "Obtén tu cifra EXACTA por 9 $",
    secure: "Pago seguro con Stripe",
    results: "Resultados en 30 segundos",
    one_time: "Pago único, sin suscripción",
    businesses: "negocios escaneados",
    snapshots_24h: "Snapshots comprados en 24h",
    scans: "auditorías IA ejecutadas",
  },
  pt: {
    tag: "Calculadora de perdas em tempo real",
    h2_a: "Os teus clientes perguntam ao ChatGPT.",
    h2_b: "Ele não sabe que existes.",
    h2_c: "Eis o preço.",
    sub: "Em 2026, 35 % das pesquisas de compra começam num motor de IA. Mete os teus números e vê o que te escapa todos os meses.",
    f_traffic: "Tráfego mensal",
    f_aov: "Valor médio do pedido",
    f_vis: "Visibilidade IA",
    monthly_lost: "Receita perdida mensal",
    annual: "São",
    annual_b: "por ano. Cada mês de espera,",
    annual_c: "passa ao lado da tua caixa.",
    cta: "Obtém o teu número EXACTO por 9 $",
    secure: "Pagamento Stripe seguro",
    results: "Resultados em 30 segundos",
    one_time: "Pagamento único, sem subscrição",
    businesses: "empresas analisadas",
    snapshots_24h: "Snapshots comprados nas últimas 24h",
    scans: "auditorias IA executadas",
  },
  nl: {
    tag: "Realtime verlies-calculator",
    h2_a: "Je klanten vragen ChatGPT.",
    h2_b: "Hij weet niet dat je bestaat.",
    h2_c: "Dit is wat het kost.",
    sub: "In 2026 begint 35 % van de koopvragen al in een AI-engine. Voer je cijfers in en zie wat je elke maand mist.",
    f_traffic: "Maandelijks verkeer",
    f_aov: "Gemiddelde orderwaarde",
    f_vis: "AI-zichtbaarheid",
    monthly_lost: "Maandelijks verloren omzet",
    annual: "Dat is",
    annual_b: "per jaar. Elke maand wachten,",
    annual_c: "loopt langs je kassa.",
    cta: "Krijg jouw EXACTE getal voor $9",
    secure: "Veilige Stripe-checkout",
    results: "Resultaat in 30 seconden",
    one_time: "Eenmalige betaling, geen abonnement",
    businesses: "bedrijven gescand",
    snapshots_24h: "Snapshots gekocht in 24h",
    scans: "AI-audits uitgevoerd",
  },
  sv: {
    tag: "Förlustkalkylator i realtid",
    h2_a: "Dina kunder frågar ChatGPT.",
    h2_b: "Den vet inte att du finns.",
    h2_c: "Här är prislappen.",
    sub: "År 2026 startar 35 % av köpfrågorna redan i en AI-motor. Skriv in dina siffror och se vad du läcker varje månad.",
    f_traffic: "Månadstrafik",
    f_aov: "Genomsnittligt ordervärde",
    f_vis: "AI-synlighet",
    monthly_lost: "Förlorad månadsintäkt",
    annual: "Det är",
    annual_b: "per år. Varje månad du väntar,",
    annual_c: "går förbi din kassa.",
    cta: "Få ditt EXAKTA tal för 9 $",
    secure: "Säker Stripe-betalning",
    results: "Resultat på 30 sekunder",
    one_time: "Engångsbetalning, ingen prenumeration",
    businesses: "företag skannade",
    snapshots_24h: "Snapshots köpta senaste 24h",
    scans: "AI-revisioner körda",
  },
  no: {
    tag: "Sanntids tap-kalkulator",
    h2_a: "Kundene dine spør ChatGPT.",
    h2_b: "Den vet ikke at du finnes.",
    h2_c: "Her er prislappen.",
    sub: "I 2026 starter 35 % av kjøpssøk allerede i en AI-motor. Legg inn tallene dine og se hva du lekker hver måned.",
    f_traffic: "Månedlig trafikk",
    f_aov: "Gj.snittlig ordreverdi",
    f_vis: "AI-synlighet",
    monthly_lost: "Tapt månedlig omsetning",
    annual: "Det er",
    annual_b: "per år. Hver måned du venter,",
    annual_c: "går forbi kassen din.",
    cta: "Få ditt EKSAKTE tall for 9 $",
    secure: "Sikker Stripe-betaling",
    results: "Resultat på 30 sekunder",
    one_time: "Engangsbetaling, ingen abonnement",
    businesses: "selskaper skannet",
    snapshots_24h: "Snapshots kjøpt siste 24t",
    scans: "AI-revisjoner gjennomført",
  },
  da: {
    tag: "Realtids tab-beregner",
    h2_a: "Dine kunder spørger ChatGPT.",
    h2_b: "Den ved ikke, at du eksisterer.",
    h2_c: "Her er prisskiltet.",
    sub: "I 2026 starter 35 % af købssøgninger allerede i en AI-motor. Indtast dine tal og se, hvad du mister hver måned.",
    f_traffic: "Månedlig trafik",
    f_aov: "Gennemsnitlig ordreværdi",
    f_vis: "AI-synlighed",
    monthly_lost: "Tabt månedlig omsætning",
    annual: "Det er",
    annual_b: "om året. Hver måned du venter,",
    annual_c: "går forbi din kasse.",
    cta: "Få dit PRÆCISE tal for 9 $",
    secure: "Sikker Stripe-betaling",
    results: "Resultat på 30 sekunder",
    one_time: "Engangsbetaling, intet abonnement",
    businesses: "virksomheder scannet",
    snapshots_24h: "Snapshots købt sidste 24t",
    scans: "AI-revisioner gennemført",
  },
  fi: {
    tag: "Reaaliaikainen tappion laskuri",
    h2_a: "Asiakkaasi kysyvät ChatGPT:ltä.",
    h2_b: "Se ei tiedä, että olet olemassa.",
    h2_c: "Tässä on hintalappu.",
    sub: "Vuonna 2026 jo 35 % ostohauista alkaa AI-moottorista. Syötä lukusi ja katso, paljonko menetät kuukaudessa.",
    f_traffic: "Kuukausiliikenne",
    f_aov: "Keskimääräinen tilausarvo",
    f_vis: "AI-näkyvyys",
    monthly_lost: "Menetetty kuukausituotto",
    annual: "Se on",
    annual_b: "vuodessa. Joka odotuskuukausi,",
    annual_c: "kävelee ohi kassasi.",
    cta: "Hanki TARKKA lukusi 9 $:lla",
    secure: "Turvallinen Stripe-maksu",
    results: "Tulos 30 sekunnissa",
    one_time: "Kertamaksu, ei tilausta",
    businesses: "yritystä skannattu",
    snapshots_24h: "Snapshotia ostettu 24t",
    scans: "AI-auditointia suoritettu",
  },
  ro: {
    tag: "Calculator de pierderi în timp real",
    h2_a: "Clienții tăi întreabă ChatGPT.",
    h2_b: "Nu știe că exiști.",
    h2_c: "Iată prețul.",
    sub: "În 2026, 35 % din căutările de cumpărare încep deja într-un motor AI. Introdu cifrele tale și vezi cât pierzi lunar.",
    f_traffic: "Trafic lunar",
    f_aov: "Valoarea medie a comenzii",
    f_vis: "Vizibilitate AI",
    monthly_lost: "Venit pierdut lunar",
    annual: "Adică",
    annual_b: "pe an. În fiecare lună de așteptare,",
    annual_c: "trec pe lângă casa ta.",
    cta: "Obține cifra EXACTĂ pentru 9 $",
    secure: "Plată Stripe securizată",
    results: "Rezultat în 30 de secunde",
    one_time: "Plată unică, fără abonament",
    businesses: "afaceri scanate",
    snapshots_24h: "Snapshot-uri cumpărate în 24h",
    scans: "audituri AI rulate",
  },
  bg: {
    tag: "Калкулатор на загубите в реално време",
    h2_a: "Клиентите ти питат ChatGPT.",
    h2_b: "Той не знае, че съществуваш.",
    h2_c: "Ето цената.",
    sub: "През 2026 г. 35 % от покупателните търсения вече започват в AI двигател. Въведи числата си и виж колко губиш месечно.",
    f_traffic: "Месечен трафик",
    f_aov: "Средна стойност на поръчка",
    f_vis: "AI видимост",
    monthly_lost: "Изгубени месечни приходи",
    annual: "Това са",
    annual_b: "годишно. Всеки месец чакане,",
    annual_c: "минава покрай касата ти.",
    cta: "Получи ТОЧНОТО число за 9 $",
    secure: "Сигурно плащане през Stripe",
    results: "Резултат за 30 секунди",
    one_time: "Еднократно плащане, без абонамент",
    businesses: "бизнеса сканирани",
    snapshots_24h: "Snapshots купени за 24ч",
    scans: "AI одита проведени",
  },
  hr: {
    tag: "Kalkulator gubitaka u stvarnom vremenu",
    h2_a: "Tvoji kupci pitaju ChatGPT.",
    h2_b: "On ne zna da postojiš.",
    h2_c: "Evo cijene.",
    sub: "U 2026. godini 35 % kupovnih pretraga već počinje u AI tražilici. Unesi svoje brojke i vidi koliko gubiš mjesečno.",
    f_traffic: "Mjesečni promet",
    f_aov: "Prosječna vrijednost narudžbe",
    f_vis: "AI vidljivost",
    monthly_lost: "Mjesečni izgubljeni prihod",
    annual: "To je",
    annual_b: "godišnje. Svaki mjesec čekanja,",
    annual_c: "prolazi pored tvoje blagajne.",
    cta: "Saznaj TOČAN broj za 9 $",
    secure: "Sigurna Stripe naplata",
    results: "Rezultat za 30 sekundi",
    one_time: "Jednokratno plaćanje, bez pretplate",
    businesses: "tvrtki skenirano",
    snapshots_24h: "Snapshot-a kupljeno u 24h",
    scans: "AI revizija pokrenuto",
  },
  uk: {
    tag: "Калькулятор втрат у реальному часі",
    h2_a: "Твої клієнти запитують ChatGPT.",
    h2_b: "Він не знає, що ти існуєш.",
    h2_c: "Ось ціна цього.",
    sub: "У 2026 році 35 % купівельних запитів уже починаються в AI-двигуні. Введи свої цифри й побач, скільки втрачаєш щомісяця.",
    f_traffic: "Місячний трафік",
    f_aov: "Середній чек",
    f_vis: "AI видимість",
    monthly_lost: "Втрачений місячний дохід",
    annual: "Це",
    annual_b: "на рік. Кожен місяць очікування,",
    annual_c: "проходять повз твою касу.",
    cta: "Отримай ТОЧНЕ число за 9 $",
    secure: "Безпечна оплата через Stripe",
    results: "Результат за 30 секунд",
    one_time: "Разова оплата, без підписки",
    businesses: "бізнесів просканувано",
    snapshots_24h: "Snapshot-ів куплено за 24г",
    scans: "AI-аудитів проведено",
  },
  tr: {
    tag: "Gerçek zamanlı kayıp hesaplayıcı",
    h2_a: "Müşterilerin ChatGPT'ye soruyor.",
    h2_b: "O senin var olduğunu bilmiyor.",
    h2_c: "İşte bedeli.",
    sub: "2026'da satın alma araştırma sorgularının %35'i artık bir AI motorunda başlıyor. Rakamlarını gir ve her ay neyi sızdırdığını gör.",
    f_traffic: "Aylık trafik",
    f_aov: "Ortalama sipariş tutarı",
    f_vis: "AI görünürlüğü",
    monthly_lost: "Aylık kayıp gelir",
    annual: "Bu",
    annual_b: "yılda. Beklediğin her ay,",
    annual_c: "kasanın yanından geçip gidiyor.",
    cta: "TAM sayını 9 $'a al",
    secure: "Güvenli Stripe ödemesi",
    results: "30 saniyede sonuç",
    one_time: "Tek seferlik ödeme, abonelik yok",
    businesses: "işletme tarandı",
    snapshots_24h: "Son 24 saatte Snapshot satın alındı",
    scans: "AI denetimi çalıştırıldı",
  },
  ja: {
    tag: "リアルタイム損失計算機",
    h2_a: "あなたの顧客はChatGPTに尋ねている。",
    h2_b: "ChatGPTはあなたの存在を知らない。",
    h2_c: "その代償はこれ。",
    sub: "2026年、購買リサーチの35%はすでにAIエンジンから始まる。数字を入力して、毎月何が漏れているか確認しよう。",
    f_traffic: "月間トラフィック",
    f_aov: "平均注文額",
    f_vis: "AI可視性",
    monthly_lost: "月間損失収益",
    annual: "これは",
    annual_b: "年間。待つ毎月、",
    annual_c: "あなたのレジを素通りする。",
    cta: "正確な数字を9ドルで取得",
    secure: "Stripe安全決済",
    results: "30秒で結果",
    one_time: "一度きりの支払い、サブスクなし",
    businesses: "ビジネスをスキャン",
    snapshots_24h: "過去24時間のSnapshot購入",
    scans: "AI監査を実行",
  },
  ko: {
    tag: "실시간 손실 계산기",
    h2_a: "고객들이 ChatGPT에게 묻고 있어요.",
    h2_b: "ChatGPT는 당신의 존재를 모릅니다.",
    h2_c: "그 대가가 여기 있어요.",
    sub: "2026년, 구매 리서치 쿼리의 35%가 이미 AI 엔진에서 시작됩니다. 수치를 입력하고 매달 무엇을 잃고 있는지 확인하세요.",
    f_traffic: "월간 트래픽",
    f_aov: "평균 주문 가치",
    f_vis: "AI 가시성",
    monthly_lost: "월간 손실 매출",
    annual: "이는",
    annual_b: "연간. 기다리는 매달,",
    annual_c: "이 당신의 계산대를 지나갑니다.",
    cta: "정확한 숫자를 $9에 받으세요",
    secure: "안전한 Stripe 결제",
    results: "30초 만에 결과",
    one_time: "일회성 결제, 구독 없음",
    businesses: "비즈니스 스캔됨",
    snapshots_24h: "지난 24시간 Snapshot 구매",
    scans: "AI 감사 실행됨",
  },
  zh: {
    tag: "实时损失计算器",
    h2_a: "你的客户在问ChatGPT。",
    h2_b: "它不知道你的存在。",
    h2_c: "代价就在这里。",
    sub: "2026年，35%的购买调研查询已经从AI引擎开始。输入你的数据，看看每个月在流失什么。",
    f_traffic: "月度流量",
    f_aov: "平均订单价值",
    f_vis: "AI 可见度",
    monthly_lost: "月度损失收入",
    annual: "这是",
    annual_b: "每年。每等一个月，",
    annual_c: "都从你的收银台溜走。",
    cta: "用 9 美元获取精确数字",
    secure: "安全的Stripe结账",
    results: "30 秒出结果",
    one_time: "一次性付款，无订阅",
    businesses: "已扫描企业",
    snapshots_24h: "过去24小时购买的Snapshot",
    scans: "AI 审计已运行",
  },
  hi: {
    tag: "रीयल-टाइम लॉस कैलकुलेटर",
    h2_a: "आपके ग्राहक ChatGPT से पूछ रहे हैं।",
    h2_b: "उसे पता नहीं कि आप मौजूद हैं।",
    h2_c: "यह रही कीमत।",
    sub: "2026 में, खरीद-शोध प्रश्नों के 35% पहले से ही एक AI इंजन के अंदर शुरू होते हैं। अपने आंकड़े डालें और देखें कि हर महीने क्या खो रहा है।",
    f_traffic: "मासिक ट्रैफ़िक",
    f_aov: "औसत ऑर्डर मूल्य",
    f_vis: "AI दृश्यता",
    monthly_lost: "मासिक खोई राजस्व",
    annual: "यह है",
    annual_b: "प्रति वर्ष। हर महीना जो आप प्रतीक्षा करते हैं,",
    annual_c: "आपकी कैश से गुज़र जाता है।",
    cta: "$9 में अपना सटीक नंबर पाएँ",
    secure: "सुरक्षित Stripe चेकआउट",
    results: "30 सेकंड में परिणाम",
    one_time: "एकमुश्त भुगतान, कोई सदस्यता नहीं",
    businesses: "व्यवसाय स्कैन किए गए",
    snapshots_24h: "पिछले 24 घंटों में खरीदे गए Snapshots",
    scans: "AI ऑडिट चलाए गए",
  },
  ar: {
    tag: "حاسبة الخسائر الفورية",
    h2_a: "عملاؤك يسألون ChatGPT.",
    h2_b: "إنه لا يعرف أنك موجود.",
    h2_c: "هذه هي التكلفة.",
    sub: "في عام 2026، يبدأ 35٪ من استفسارات أبحاث الشراء بالفعل داخل محرك ذكاء اصطناعي. أدخل أرقامك وشاهد ما تخسره كل شهر.",
    f_traffic: "حركة الزيارات الشهرية",
    f_aov: "متوسط قيمة الطلب",
    f_vis: "ظهور الذكاء الاصطناعي",
    monthly_lost: "الإيرادات الشهرية المفقودة",
    annual: "وهذا",
    annual_b: "في السنة. كل شهر تنتظر فيه،",
    annual_c: "يمر بجانب صندوق دفعك.",
    cta: "احصل على رقمك الدقيق مقابل 9$",
    secure: "دفع Stripe آمن",
    results: "النتيجة خلال 30 ثانية",
    one_time: "دفعة واحدة، لا اشتراك",
    businesses: "نشاط تجاري تم فحصه",
    snapshots_24h: "Snapshots مُشتراة في آخر 24 ساعة",
    scans: "عمليات تدقيق ذكاء اصطناعي تم تشغيلها",
  },
  id: {
    tag: "Kalkulator kerugian real-time",
    h2_a: "Pelangganmu bertanya ke ChatGPT.",
    h2_b: "Ia tidak tahu kamu ada.",
    h2_c: "Inilah harganya.",
    sub: "Di 2026, 35 % kueri riset pembelian sudah dimulai di mesin AI. Masukkan angkamu dan lihat berapa yang bocor tiap bulan.",
    f_traffic: "Trafik bulanan",
    f_aov: "Nilai rata-rata pesanan",
    f_vis: "Visibilitas AI",
    monthly_lost: "Pendapatan hilang per bulan",
    annual: "Itu",
    annual_b: "per tahun. Setiap bulan kamu menunggu,",
    annual_c: "lewat begitu saja dari kasirmu.",
    cta: "Dapatkan angka PERSIS-mu seharga $9",
    secure: "Checkout Stripe aman",
    results: "Hasil dalam 30 detik",
    one_time: "Pembayaran sekali, tanpa langganan",
    businesses: "bisnis dipindai",
    snapshots_24h: "Snapshot dibeli dalam 24 jam",
    scans: "audit AI dijalankan",
  },
};
const tCopy = (lang) => COPY[lang] || COPY.en;

/**
 * HomepageLossHero — the conversion-engine hero block.
 */
export default function HomepageLossHero() {
  const { language } = useLanguage();
  const C = tCopy(language);
  const [result, setResult] = useState(null);
  const [traffic, setTraffic] = useState(10_000);
  const [aov, setAov] = useState(80);
  const [visibility, setVisibility] = useState(25);
  const [stats, setStats] = useState(null);

  // Load social-proof stats once on mount (cached server-side 60s).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/social-proof/hero-stats`)
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => {/* hero must work without stats */});
    return () => { cancelled = true; };
  }, []);

  // Compute on mount + every time inputs change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/wasted-calculator/compute`, {
          monthly_traffic: Number(traffic) || undefined,
          avg_order_value: Number(aov) || undefined,
          ai_visibility_score: Number(visibility),
          conversion_rate: 0.02,
        });
        if (!cancelled) setResult(r.data);
      } catch {/* fail silently — hero must never break */}
    })();
    return () => { cancelled = true; };
  }, [traffic, aov, visibility]);

  const fmtUsd = (n) => {
    // Display in USD-style formatting since we're going global.
    const v = Number(n) || 0;
    return v.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };

  return (
    <section
      data-testid="homepage-loss-hero"
      className="relative py-20 px-6 bg-gradient-to-b from-[#0a0a0f] via-[#160820] to-[#0a0a0f] border-y border-purple-900/30 overflow-hidden"
    >
      {/* Background grid pattern for depth */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(168,85,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(168,85,247,1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span
            data-testid="loss-hero-tag"
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-500/15 text-red-300 rounded-full text-xs font-bold font-['JetBrains_Mono'] uppercase tracking-widest border border-red-500/20"
          >
            <TrendingDown className="w-3.5 h-3.5" />
            {C.tag}
          </span>
          <h2
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-white leading-[1.05]"
            style={{ textWrap: "balance" }}
          >
            {C.h2_a}
            <br />
            {C.h2_b}
            <br />
            <span className="text-red-500">{C.h2_c}</span>
          </h2>
          <p className="mt-5 text-base lg:text-lg text-gray-400 max-w-2xl mx-auto">
            {C.sub}
          </p>
        </div>

        {/* Inputs strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto mb-6">
          <SliderField
            label={C.f_traffic}
            value={traffic}
            min={500}
            max={500_000}
            step={500}
            onChange={setTraffic}
            format={fmtUsd}
            testid="loss-hero-traffic"
          />
          <SliderField
            label={C.f_aov}
            value={aov}
            min={5}
            max={2000}
            step={5}
            onChange={setAov}
            format={(v) => `$${fmtUsd(v)}`}
            testid="loss-hero-aov"
          />
          <SliderField
            label={C.f_vis}
            value={visibility}
            min={0}
            max={100}
            step={1}
            onChange={setVisibility}
            format={(v) => `${v}/100`}
            testid="loss-hero-visibility"
          />
        </div>

        {/* The number */}
        <div
          data-testid="loss-hero-result"
          className="text-center bg-gradient-to-br from-red-950/40 to-black/40 border border-red-500/30 rounded-3xl p-8 lg:p-12 backdrop-blur-sm"
        >
          <p className="text-xs font-bold text-red-400 uppercase tracking-[0.3em] mb-3 font-['JetBrains_Mono']">
            {C.monthly_lost}
          </p>
          <p
            data-testid="loss-hero-monthly"
            className="text-7xl sm:text-8xl lg:text-9xl font-black text-red-500 tabular-nums tracking-tight"
            style={{ textShadow: "0 0 80px rgba(239,68,68,0.4)" }}
          >
            ${result ? fmtUsd(result.monthly_lost_revenue) : "—"}
          </p>
          <p className="mt-4 text-sm text-gray-400">
            {C.annual}{" "}
            <span className="text-red-300 font-bold tabular-nums" data-testid="loss-hero-annual">
              ${result ? fmtUsd(result.annual_lost_revenue) : "—"}
            </span>{" "}
            {C.annual_b} ${result ? fmtUsd(result.monthly_lost_revenue) : "—"} {C.annual_c}
          </p>
        </div>

        {/* The CTA */}
        <div className="mt-10 max-w-2xl mx-auto text-center">
          <Link
            to="/snapshot"
            data-testid="loss-hero-cta-snapshot"
            className="inline-flex items-center gap-3 px-8 py-5 bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 rounded-2xl text-lg font-black hover:from-amber-300 hover:to-yellow-300 transition-all shadow-[0_10px_40px_-5px_rgba(251,191,36,0.5)] hover:shadow-[0_15px_50px_-5px_rgba(251,191,36,0.65)] hover:-translate-y-0.5"
          >
            <Sparkles className="w-5 h-5" />
            {C.cta}
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 text-xs text-gray-500 flex items-center justify-center gap-3">
            <span className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {C.secure}
            </span>
            <span>·</span>
            <span>{C.results}</span>
            <span>·</span>
            <span>{C.one_time}</span>
          </p>

          {/* Real-data micro stats — only render if backend returned numbers */}
          {stats && (stats.total_businesses > 0 || stats.snapshots_24h > 0) && (
            <div
              data-testid="loss-hero-stats"
              className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-gray-400"
            >
              {stats.total_businesses > 0 && (
                <span
                  data-testid="loss-hero-stat-businesses"
                  className="flex items-center gap-1.5"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="tabular-nums font-semibold text-gray-200">
                    {stats.total_businesses.toLocaleString("en-US")}
                  </span>{" "}
                  {C.businesses}
                </span>
              )}
              {stats.snapshots_24h > 0 && (
                <span
                  data-testid="loss-hero-stat-snapshots-24h"
                  className="flex items-center gap-1.5"
                >
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="tabular-nums font-semibold text-gray-200">
                    {stats.snapshots_24h}
                  </span>{" "}
                  {C.snapshots_24h}
                </span>
              )}
              {stats.total_scans > 0 && (
                <span
                  data-testid="loss-hero-stat-scans"
                  className="flex items-center gap-1.5"
                >
                  <span className="tabular-nums font-semibold text-gray-200">
                    {stats.total_scans.toLocaleString("en-US")}
                  </span>{" "}
                  {C.scans}
                </span>
              )}
            </div>
          )}

          {/* Engine support strip — real, not fake "as seen on" */}
          <div
            data-testid="loss-hero-engines"
            className="mt-10 pt-8 border-t border-white/5"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-4 font-['JetBrains_Mono']">
              Scans every major AI engine
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              <EngineBadge label="ChatGPT" />
              <EngineBadge label="Claude" />
              <EngineBadge label="Gemini" />
              <EngineBadge label="Perplexity" />
              <EngineBadge label="Google AI Overview" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EngineBadge({ label }) {
  return (
    <div
      data-testid={`loss-hero-engine-${label.toLowerCase().split(" ")[0]}`}
      className="text-sm font-bold text-gray-300 tracking-tight"
    >
      {label}
    </div>
  );
}

function SliderField({ label, value, min, max, step, onChange, format, testid }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-['JetBrains_Mono']">
          {label}
        </label>
        <span
          data-testid={`${testid}-value`}
          className="text-sm font-bold text-white tabular-nums font-['JetBrains_Mono']"
        >
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-red-500"
      />
    </div>
  );
}
